import { useConfiguratorProductCode } from "~/features/configurator/common/useConfiguratorType";
import {
  ConfiguratorType,
  getConfiguratorFamilyFromProductCode,
  getConfiguratorTypeFromProductCode,
} from "~/domains/configurator/types/ConfiguratorType";
import { useTranslation } from "react-i18next";
import { type TFunction, type WithT } from "i18next";
import { useSelector } from "react-redux";
import { selectAllSteps } from "~/features/configurator/Linear/redux/selectors";

type ReturnType = {
  t: TFunction;
  tPrefix: string;
  renderStepName: (index: number) => string;
};

const UNDEFINED_RETURN_TYPE: UndefinedReturnType = {
  t: undefined,
  tPrefix: undefined,
  renderStepName: undefined,
};
type UndefinedReturnType = Record<keyof ReturnType, undefined>;
export function useXmlConfiguratorTranslations(): ReturnType | UndefinedReturnType {
  const productCode = useConfiguratorProductCode();
  const configuratorType = getConfiguratorTypeFromProductCode(productCode);
  if (configuratorType === null) {
    return UNDEFINED_RETURN_TYPE;
  }
  const config = XML_TRANSLATION_SHEETS[configuratorType];
  if (!config) {
    return UNDEFINED_RETURN_TYPE;
  }
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useForcedConfiguratorTranslations(productCode);
}

export function useForcedConfiguratorTranslations(productCode: string): ReturnType | UndefinedReturnType {
  const configuratorType = getConfiguratorTypeFromProductCode(productCode);
  const configuratorFamily = getConfiguratorFamilyFromProductCode(productCode);
  if (configuratorType === null) {
    return UNDEFINED_RETURN_TYPE;
  }
  const config = XML_TRANSLATION_SHEETS[configuratorType];
  if (!configuratorType || !config) {
    return UNDEFINED_RETURN_TYPE;
  }
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { t: originalT } = useTranslation(config?.sheetName);
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const steps = useSelector(selectAllSteps);
  const t = ((key: string, options?: object) =>
    originalT(key, { context: configuratorFamily, ...options })) as WithT["t"];
  return {
    t,
    tPrefix: config?.tPrefix,
    renderStepName: (index: number) => {
      return t(steps[index].name);
    },
  };
}

export const XML_TRANSLATION_SHEETS = {
  [ConfiguratorType.System]: {
    sheetName: "unitConfigurator",
    tPrefix: "configurator.unit",
  },
  [ConfiguratorType.Rail]: {
    sheetName: "railConfigurator",
    tPrefix: "configurator.rail",
  },
  [ConfiguratorType.Carriage]: {
    sheetName: "carriageConfigurator",
    tPrefix: "configurator.carriage",
  },
  [ConfiguratorType.MetricShaft]: {
    sheetName: "shaftConfigurator",
    tPrefix: "configurator.shaft",
  },
  [ConfiguratorType.InchShaft]: {
    sheetName: "shaftConfigurator",
    tPrefix: "configurator.shaft",
  },
};
